import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, Observable } from 'rxjs';

import { CurrentSiteState } from '@ppg/configuration';
import { ClientSiteSettingsKeys, ResourceItem } from '@ppg/core/enums';
import { ColorFamily, ColorInfoValid } from '@ppg/core/models';
import { ColorFamilyInfoService, ColorInfoService } from '@ppg/shared/color/services';

import { ColorFamilyGroupNames } from '../models/color-family-group-names.enum';
import { ColorInfoGroup } from '../models/color-group.model';

@Injectable({ providedIn: 'root' })
export class ColorFamilyService {
  private readonly colorInfoService = inject(ColorInfoService);
  private readonly colorFamilyInfoService = inject(ColorFamilyInfoService);
  private readonly currentSiteState = inject(CurrentSiteState);
  private readonly destroyRef = inject(DestroyRef);

  readonly colorFamiliesGroupedByType = signal<Record<string, ColorFamily[]>>({});

  constructor() {
    this.getColorFamilies();
  }

  getGroupedColorsPerFamily(colorFamilyId: string): Observable<ColorInfoGroup[]> {
    return this.groupColorsByTone(this.colorInfoService.getColorsPerFamily(colorFamilyId));
  }

  isFamilyGroupingEnabled(): boolean {
    return this.currentSiteState.getSiteFlag(ClientSiteSettingsKeys.ColorJourneyFamilyGroupingEnabled);
  }

  isPaginatorHidden(): boolean {
    return this.currentSiteState.getSiteFlag(ClientSiteSettingsKeys.ColorJourneyFamiliesPaginatorHidden);
  }

  getColorsPerFamily(colorFamilyId: string) {
    return this.colorInfoService.getColorsPerFamily(colorFamilyId);
  }

  private groupColorsByTone(colors$: Observable<ColorInfoValid[]>): Observable<ColorInfoGroup[]> {
    const initialGroups = this.initializeColorGroups();
    const lightThreshold = this.getLightThreshold();
    const darkThreshold = this.getDarkThreshold();

    return colors$.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((colors) => {
        return colors.reduce((groups, color) => {
          if (this.isBestSeller(color)) {
            groups[0].colors.push(color);
          }

          if (this.isLightColor(color, lightThreshold)) {
            groups[1].colors.push(color);
          } else if (this.isMidtoneColor(color, lightThreshold, darkThreshold)) {
            groups[2].colors.push(color);
          } else {
            groups[3].colors.push(color);
          }
          return groups;
        }, initialGroups);
      }),
    );
  }

  private isBestSeller(color: ColorInfoValid): boolean {
    return color.isBestSeller;
  }

  private isLightColor(color: ColorInfoValid, lightnessThreshold: number): boolean {
    return color.lightness !== undefined && color.lightness > lightnessThreshold;
  }

  private isMidtoneColor(color: ColorInfoValid, lightnessThreshold: number, midtoneThreshold: number): boolean {
    return (
      color.lightness !== undefined && color.lightness >= midtoneThreshold && color.lightness <= lightnessThreshold
    );
  }

  private initializeColorGroups(): ColorInfoGroup[] {
    return [
      {
        id: 0,
        groupName: ColorFamilyGroupNames.MostPopular,
        colors: [],
        labelResourceKey: ResourceItem.COLORS_FAMILIES_CATEGORY_SHADOW_MOST_POPULAR,
      },
      {
        id: 1,
        groupName: ColorFamilyGroupNames.LightColors,
        colors: [],
        labelResourceKey: ResourceItem.COLORS_FAMILIES_CATEGORY_SHADOW_LIGHT,
      },
      {
        id: 2,
        groupName: ColorFamilyGroupNames.MidtoneColors,
        colors: [],
        labelResourceKey: ResourceItem.COLORS_FAMILIES_CATEGORY_SHADOW_MIDTONE,
      },
      {
        id: 3,
        groupName: ColorFamilyGroupNames.DarkColors,
        colors: [],
        labelResourceKey: ResourceItem.COLORS_FAMILIES_CATEGORY_SHADOW_DARK,
      },
    ];
  }

  private getColorFamilies() {
    if (!Object.keys(this.colorFamiliesGroupedByType()).length) {
      this.colorFamilyInfoService
        .getColorFamiliesGroupedByType()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((familiesGroupedByType) => {
          this.colorFamiliesGroupedByType.set(familiesGroupedByType);
        });
    }
  }

  private getLightThreshold(): number {
    return Number(this.currentSiteState.getSiteSettingValueByKey(ClientSiteSettingsKeys.ColorJourneyLightThreshold));
  }

  private getDarkThreshold(): number {
    return Number(this.currentSiteState.getSiteSettingValueByKey(ClientSiteSettingsKeys.ColorJourneyDarkThreshold));
  }
}
